import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../services/api';
import type { BoardroomChat, BoardroomChatChanged } from '../../types';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { QuickPromptBar } from '../QuickPromptBar';
import ChatMarkdown from '../common/ChatMarkdown';
import ItemRefText from './ItemRefText';
import ContextUsageChip from '../ContextUsageChip';
import { AgentErrorBanner, AgentStatusChip } from '../common/AgentErrorBanner';
import { starterPrompts, type RoomPromptState } from './chatPrompts';

/**
 * The room chat — how a person aims their own room.
 *
 * THREE INDEPENDENT REFRESH TRIGGERS, and the redundancy is deliberate: this
 * codebase already learned why (see `SiteScaffoldChatPane`, and `SiteParsers`
 * before it). Keying "a round finished" off the poll alone silently never
 * fires, because the event's refetch flips the status to idle, the poll effect
 * re-runs, cleanup clears the interval, and the transition is never observed.
 * So: the thinking -> idle transition watched here, the room's own websocket
 * event, and a 2s poll while thinking as the missed-event fallback.
 *
 * Unlike the other panes, the event CARRIES its payload: the room event says
 * which surfaces the turn touched (`changed`), so a reply that only answered a
 * question repaints nothing, and a turn that set the axes repaints the frame
 * alone. A blanket refetch of a room mid-dig is not cheap.
 */
export function BoardroomChatPane({
  boardroomId,
  room,
  draft,
  onDraftUsed,
  say,
  onSayUsed,
  welcome = true,
  onConversation,
  onSaid,
  onChanged,
  onBusyChange,
  onCollapse,
  controls,
  view,
  onOpenItem,
}: {
  /** Open an item the chat names. With it, every "#36948" in a message is a
   *  link to that item's page. */
  onOpenItem?: (itemId: number) => void;
  boardroomId: number;
  /** What the person has open, said to the chat with each message (`viewPhrase`). */
  view?: string | null;
  /** What the chips should offer — see `chatPrompts`. */
  room: RoomPromptState;
  /** A line the page wants said — put in the box, NOT sent. A card's "go get
   *  these" is an ask worth qualifying before it spends anything. */
  draft?: string | null;
  onDraftUsed?: () => void;
  /** A line the page wants SENT, as if typed: the start form's first message. */
  say?: string | null;
  onSayUsed?: () => void;
  /** The new-room welcome. Off where the page shows its own first screen. */
  welcome?: boolean;
  /** The chat has a conversation in it: said once it does, including on reload. */
  onConversation?: () => void;
  /** What the person just said. Used while a room is being opened, to rank the
   *  shapes it could start from against their own words. */
  onSaid?: (text: string) => void;
  /** What this turn touched, so the page can reconcile just those parts. */
  onChanged: (changed: BoardroomChatChanged) => void;
  onBusyChange?: (busy: boolean) => void;
  /** Fold it to the edge tab. Absent when the pane is not dockable. */
  onCollapse?: () => void;
  /** Header controls from the panel it sits in — Unpin and × when docked. */
  controls?: ReactNode;
}) {
  const [chat, setChat] = useState<BoardroomChat | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [promptPage, setPromptPage] = useState(0);
  const pollRef = useRef<any>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const changedRef = useRef<BoardroomChatChanged | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await api.getBoardroomChat(boardroomId);
      setChat(next);
      return next;
    } catch {
      return null;
    }
  }, [boardroomId]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  const { subscribe } = useWebSocket();
  useEffect(
    () =>
      subscribe((event: any) => {
        if (event?.event_type !== 'boardroom' || event.boardroom_id !== boardroomId) return;
        if (event.status !== 'chat' || !event.chat) return;
        // Remember what the turn changed before the refetch races it — the
        // transition effect below is what notifies the page.
        if (event.chat.status === 'replied' && event.chat.changed) {
          changedRef.current = event.chat.changed as BoardroomChatChanged;
        }
        load();
      }),
    [subscribe, boardroomId, load],
  );

  useEffect(() => {
    clearInterval(pollRef.current);
    if (chat?.status === 'thinking') pollRef.current = setInterval(load, 2000);
    return () => clearInterval(pollRef.current);
  }, [chat?.status, load]);

  const wasThinkingRef = useRef(false);
  useEffect(() => {
    const thinking = chat?.status === 'thinking';
    if (wasThinkingRef.current && !thinking) {
      // No event told us what moved: assume everything did. A needless repaint
      // costs one request; a missed one leaves a person reading a room that has
      // already been re-aimed.
      onChanged(
        changedRef.current ?? {
          room: true,
          frame: true,
          seats: true,
          tables: true,
          questions: 0,
          started_run_id: 0,
          stopped_run_id: 0,
        },
      );
      changedRef.current = null;
    }
    wasThinkingRef.current = thinking;
  }, [chat?.status, onChanged]);

  useEffect(() => {
    // Within the message list only: `scrollIntoView` also scrolled the page
    // around the chat, so a new room opened halfway down its start form.
    const list = listRef.current;
    if (list) list.scrollTo?.({ top: list.scrollHeight, behavior: 'smooth' });
  }, [chat?.messages.length, chat?.status]);

  const send = async (text?: string) => {
    const body = (text ?? input).trim();
    if (!body || busy) return;
    if (text === undefined) setInput('');
    setBusy(true);
    setError('');
    onSaid?.(body);
    try {
      setChat(await api.sendBoardroomChat(boardroomId, body, view));
    } catch (e: any) {
      setError(e?.message || 'Could not send that message.');
    } finally {
      setBusy(false);
    }
  };

  // ANSWERING IS ONE CLICK AND IT STICKS. The reply carries the next card, so
  // settling one thing does not bounce the reader back to an empty panel.
  const [deciding, setDeciding] = useState(false);
  const answer = async (choice: string) => {
    const d = chat?.decision;
    if (!d || deciding) return;
    setDeciding(true);
    try {
      const out = await api.answerBoardroomDecision(boardroomId, {
        kind: d.kind,
        subject: d.subject,
        choice,
      });
      setChat(c => (c ? { ...c, decision: out.decision } : c));
      // What the room DID about it — stopping a dig or dropping an axis is
      // worth a line, not a silent repaint.
      onChanged({
        room: true,
        frame: d.kind === 'frame_proposal' || d.kind === 'useless_axis',
        seats: false,
        tables: d.kind === 'demand_dry',
        questions: 0,
        started_run_id: 0,
        stopped_run_id: d.kind === 'answer_stalled' ? 1 : 0,
      });
    } catch (e: any) {
      setError(e?.message || 'Could not record that.');
    } finally {
      setDeciding(false);
    }
  };

  // STOPPING GIVES THE CHAT BACK. A worker that died, a turn stuck on a slow
  // tool, or a reply heading somewhere useless all leave the pane saying
  // "thinking" with the box disabled — and waiting out the five-minute
  // auto-recover is a long time to watch a spinner you know is wrong.
  const [stopping, setStopping] = useState(false);
  const stop = async () => {
    if (stopping) return;
    setStopping(true);
    setError('');
    try {
      setChat(await api.stopBoardroomChat(boardroomId));
    } catch (e: any) {
      setError(e?.message || 'Could not stop that.');
    } finally {
      setStopping(false);
    }
  };

  const newChat = async () => {
    setBusy(true);
    try {
      setChat(await api.clearBoardroomChat(boardroomId));
    } catch (e: any) {
      setError(e?.message || 'Could not start a new conversation.');
    } finally {
      setBusy(false);
    }
  };

  const thinking = chat?.status === 'thinking';
  const messages = chat?.messages || [];
  const talking = messages.length > 0 || chat?.status === 'thinking';
  useEffect(() => {
    if (talking) onConversation?.();
  }, [talking, onConversation]);

  useEffect(() => {
    onBusyChange?.(thinking || busy);
  }, [thinking, busy, onBusyChange]);

  // Take the page's draft once, and only over an empty box: a half-typed
  // message is the user's, and a card must not overwrite it.
  useEffect(() => {
    if (!draft) return;
    setInput(prev => (prev.trim() ? prev : draft));
    onDraftUsed?.();
  }, [draft, onDraftUsed]);

  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    if (!say) return;
    onSayUsed?.();
    void sendRef.current(say);
  }, [say, onSayUsed]);

  return (
    <div className="flex flex-col h-full min-w-0 bg-white dark:bg-gray-800">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Ask the room</h3>
        {chat && (
          <>
            <ContextUsageChip
              contextTokens={chat.context_tokens}
              contextWindow={chat.context_window}
            />
            {/* Clearing the CONVERSATION never touches the ROOM: what was
                agreed here is already written into the question, the axes and
                the seats, and a button that quietly undid those would be the
                worst button on the page. */}
            <button
              type="button"
              onClick={newChat}
              disabled={thinking || busy || messages.length === 0}
              title="Begin a fresh conversation. The room keeps everything agreed so far; earlier messages just stop being part of the conversation."
              className="px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              New chat
            </button>
            <AgentStatusChip status={chat.status} className="ml-auto" />
            {/* THE CHAT'S OWN MINIMISE. It used to be a grey chevron that read
                as punctuation, with the real control a button at the top of the
                page a long way from the thing it opened. This is the one that
                folds it, and the rail it folds into is what brings it back. */}
            {onCollapse && (
              <button
                type="button"
                onClick={onCollapse}
                title="Minimize the chat - it folds to the edge, and everything said is kept"
                aria-label="Minimize the chat"
                data-testid="collapse-room-chat"
                className="shrink-0 rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
              >
                –<span className="sr-only">Minimize</span>
              </button>
            )}
          </>
        )}
        {controls && <div className={`flex items-center gap-2 shrink-0 ${chat ? '' : 'ml-auto'}`}>{controls}</div>}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {welcome && messages.length === 0 && !thinking && !(room.question || '').trim() && (
          // A NEW ROOM IS A MEETING THEY ARE CHAIRING. Without this, the first
          // questions ("what do you want holding at the end?") read like a form
          // to fill in, not a CEO setting the agenda for their board.
          <div
            className="text-sm text-gray-600 dark:text-gray-300 space-y-2 text-pretty"
            data-testid="new-room-welcome"
          >
            <p className="font-medium text-gray-900 dark:text-gray-100">
              Welcome to Recon. You’re the CEO.
            </p>
            <p>
              Around the table are researchers - one finds the players, one checks what
              people complain about, one measures search demand, one pushes back. They
              won’t start until you tell them what this meeting is for.
            </p>
            <p>
              You set the goal and the tone. I’ll ask a few short questions first: what you
              want to know, what you want to walk out with, and how this market should be
              compared. Then the room gets to work, and you can steer it from here.
            </p>
          </div>
        )}
        {messages.length === 0 && !thinking && (room.question || '').trim() && (
          // `text-pretty`: the browser balances the last lines, so the intro
          // doesn't leave "it." alone on a line at common widths.
          <div className="text-sm text-gray-500 dark:text-gray-400 space-y-2 text-pretty">
            <p>
              Tell me what you want to find out and I’ll set this room up for it - what it
              asks, what it compares on, and who sits in it.
            </p>
            <p>
              While it’s working, this is where you steer: chase something, drop something,
              or ask what it has found.
            </p>
          </div>
        )}

        {messages.map((message) => {
          const isHuman = message.entity_type === 'HUMAN';
          if (!message.content.trim() && message.has_tool_call) {
            return (
              <div
                key={message.id}
                className="text-xs text-gray-400 dark:text-gray-500 italic"
              >
                …working
              </div>
            );
          }
          return (
            <div
              key={message.id}
              className={`rounded-lg px-3 py-2 text-sm ${
                isHuman
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-gray-100 ml-6'
                  : 'bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 mr-6'
              }`}
            >
              <ChatMarkdown
                text={message.content}
                renderText={onOpenItem ? t => <ItemRefText text={t} onOpen={onOpenItem} /> : undefined}
              />
            </div>
          );
        })}

        {thinking && (
          // The same escape where the eye already is — a person watching a
          // spinner is looking here, not at the composer.
          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
            <span>Thinking…</span>
            <button
              type="button"
              onClick={stop}
              disabled={stopping}
              className="rounded px-1 text-xs text-red-600 hover:underline disabled:opacity-50 dark:text-red-400"
            >
              Stop
            </button>
          </div>
        )}
        <AgentErrorBanner error={chat?.last_error} className="border-b-0" />
        <AgentErrorBanner error={error} className="border-b-0" />
      </div>

      {chat?.decision && (
        <div
          className="border-t border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2"
          role="alertdialog"
          aria-label="The room needs a decision"
          data-testid="decision-card"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 text-xs font-medium text-amber-900 dark:text-amber-100">
              {chat.decision.ask}
            </div>
            {/* PUT IT ASIDE. A card that no longer applies — a question about
                a dig that has since stopped — should not have to be answered
                to go away. Recorded, so it is not asked again. */}
            <button
              type="button"
              onClick={() => answer('dismiss')}
              disabled={deciding}
              aria-label="Put this question aside"
              title="Put this question aside - it won't be asked again"
              className="icon-btn shrink-0 -mt-0.5 px-1 text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100 disabled:opacity-50"
              data-testid="decision-dismiss"
            >
              ×
            </button>
          </div>
          <div className="mt-0.5 text-xs text-amber-800 dark:text-amber-200/80">
            {chat.decision.why}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {chat.decision.options.map(o => (
              <button
                key={o.key}
                type="button"
                title={o.effect}
                disabled={deciding}
                onClick={() => answer(o.key)}
                className="rounded border border-amber-400 dark:border-amber-600 bg-white/70 dark:bg-gray-800/60 px-2 py-0.5 text-xs text-amber-900 dark:text-amber-100 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
        <QuickPromptBar
          agentPrompts={chat?.suggested_prompts}
          staticPool={starterPrompts(room)}
          page={promptPage}
          onCycle={() => setPromptPage((p) => p + 1)}
          onSend={(prompt) => {
            // A prompt ending in a space is unfinished ("Chase ") — put it in
            // the box to be completed rather than sending a verb with no object.
            if (prompt.endsWith(' ')) setInput(prompt);
            else send(prompt);
          }}
          disabled={thinking || busy}
        />
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            // NOT ONE ROOM'S EXAMPLE. "Chase the skate brands…" showed in a
            // healthcare room too.
            placeholder={
              (room.question || '').trim()
                ? 'Ask a question, or say what to look into next…'
                : 'What is this meeting about? e.g. who sells baggy jeans, and for how much'
            }
            disabled={thinking}
            className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          />
          {/* ONE BUTTON, WHERE THE HAND ALREADY IS. Stop takes Send's place
              while a reply is in flight rather than hiding in the header —
              Send is disabled then anyway, so the slot is free and the thing
              you want is under the cursor. */}
          {thinking ? (
            <button
              type="button"
              onClick={stop}
              disabled={stopping}
              // "STOP REPLY", NOT "STOP": beside a running dig, a red Stop read as
              // stopping the dig, and it never did. The dig's own Stop is in the
              // bar at the top of the room.
              title="Stop this reply and give the chat back. It does not stop a dig - use Stop the dig at the top of the room."
              className="self-end rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="chat-stop-reply"
            >
              {stopping ? 'Stopping…' : 'Stop reply'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 self-end"
            >
              Send
            </button>
          )}
        </div>
        {/* SAID, SO IT IS NOT A SURPRISE: the chat is told what is open. */}
        {view && (
          <p className="mt-1 truncate text-[11px] text-gray-400 dark:text-gray-500" title={view} data-testid="chat-sees">
            The chat sees: {view}
          </p>
        )}
      </div>
    </div>
  );
}

export default BoardroomChatPane;
